import React from 'react';

import { faPaperclipVertical } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { useGetCashCollection } from 'src/features/cashCollections/hooks/useGetCashCollection';
import { useGetParkingCashCollection } from 'src/features/cashCollections/hooks/useGetParkingCashCollection';
import { badgeCollectionStatusOptions } from 'src/features/cashCollections/utils/constants';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { formatFileSize } from 'src/features/cashCollections/utils/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/utils/messages';

type CashCollectionSidePanelProps = {
    id: string;
    parkingId?: string;
};

const CashCollectionSidePanel = ({ id, parkingId }: CashCollectionSidePanelProps) => {
    const { formatMessage, formatNumber, formatDate } = useIntl();

    const { data: cashCollectionData } = useGetCashCollection({
        id,
        config: { enabled: !!id && !parkingId },
    });

    const { data: parkingCashCollectionData } = useGetParkingCashCollection({
        id,
        parkingId: parkingId || '',
        config: { enabled: !!id && !!parkingId },
    });

    const cashCollection = React.useMemo(
        () => cashCollectionData ?? parkingCashCollectionData,
        [cashCollectionData, parkingCashCollectionData],
    );

    const sortedNominalAmounts = new Map(
        cashCollection?.nominal_amounts &&
            Object.entries(cashCollection?.nominal_amounts).sort(
                ([keyA], [keyB]) => parseFloat(keyA) - parseFloat(keyB),
            ),
    );

    const formattedDateTime =
        cashCollection?.date &&
        formatDate(new Date(cashCollection?.date), {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const isEmptyObject = (obj: any) => !obj || (typeof obj === 'object' && Object.keys(obj).length === 0);

    return (
        <>
            {cashCollection ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{cashCollection.number}</DialogTitle>
                    </div>
                    <DialogDescription>{formatMessage(cashCollectionsMessages.cashCollection)}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <div className="mt-4 grid gap-2">
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionDate)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.date}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {formattedDateTime}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionsPayStation)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.paystation}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.paystation.identifier}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionStatus)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.status}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.status && (
                                <Badge theme={badgeCollectionStatusOptions[cashCollection.status].theme}>
                                    {formatMessage(badgeCollectionStatusOptions[cashCollection.status].label)}
                                </Badge>
                            )}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionCoins)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.total_coins_amount}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.total_coins_amount &&
                                formatNumber(cashCollection?.total_coins_amount / 100, {
                                    style: 'currency',
                                    currency: cashCollection.currency,
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionBanknotes)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.total_banknotes_amount}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.total_banknotes_amount &&
                                formatNumber(cashCollection?.total_banknotes_amount / 100, {
                                    style: 'currency',
                                    currency: cashCollection.currency,
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionTotalAmount)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.total_amount}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.total_amount &&
                                formatNumber(cashCollection?.total_amount / 100, {
                                    style: 'currency',
                                    currency: cashCollection.currency,
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashCollectionCollectedBy)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.collected_by}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.collected_by}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.collectionDescription)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.description}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.description}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.cashBreakdown)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={
                                !isEmptyObject(cashCollection?.nominal_amounts)
                                    ? cashCollection?.nominal_amounts
                                    : false
                            }
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.nominal_amounts &&
                                Array.from(sortedNominalAmounts.entries()).map(([key, value], index) => {
                                    const nominationSum =
                                        value && key ? parseFloat(key as string) * (value as number) : 0.0;
                                    return (
                                        <div className="grid grid-cols-3 gap-8" key={index}>
                                            <div>
                                                {formatNumber(Number(key), {
                                                    style: 'currency',
                                                    currency: cashCollection.currency,
                                                })}
                                            </div>
                                            <div>{`x${value}`}</div>
                                            <div>
                                                {formatNumber(nominationSum, {
                                                    style: 'currency',
                                                    currency: cashCollection.currency,
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cashCollectionsMessages.evidence)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cashCollection?.files.length}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cashCollection?.files &&
                                cashCollection.files.length > 0 &&
                                cashCollection.files.map((file, idx) => {
                                    return (
                                        <div key={idx} className="mt-1 flex items-center gap-2">
                                            <a
                                                href={file.url}
                                                download={file.name}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white p-2 hover:bg-gray-100"
                                            >
                                                <div className="flex flex-row items-center justify-start gap-2">
                                                    <SquareIcon icon={faPaperclipVertical} />
                                                    <div>
                                                        <div className="max-w-[224px] truncate text-xs font-semibold">
                                                            {file.name}
                                                        </div>
                                                        <div className="text-xs font-semibold text-gray-500">
                                                            {formatFileSize(file.size_bytes)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </a>
                                        </div>
                                    );
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                </div>
            </DialogBody>
            <DialogSeparator />

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default CashCollectionSidePanel;
